import React, { useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { FaPlay, FaCircle } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Enrichment } from "@/models/enrichment";
import { getEnrichmentIcon, getTypeLabel } from "@/features/enrichments/utils";
import { createEnrichmentDragPayload } from "@/utils/drag-types";
import { usePanelNavigation } from "@/hooks/use-panel-route";
import SidebarItem, { SidebarItemAction } from "./sidebar-item";
import ResourceContextMenu from "./resource-context-menu";

interface EnrichmentItemProps {
  enrichment: Enrichment;
  onRunEnrichment?: (enrichment: Enrichment) => void;
  // Multi-select props
  /** Whether this item is selected */
  isSelected?: boolean;
  /** Ordered list of all enrichment IDs for range selection */
  orderedIds?: string[];
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
  // Drag-to-group props
  /** Callback when another enrichment is dropped on this one to create a group */
  onDropToCreateGroup?: (draggedEnrichmentId: string, targetEnrichmentId: string) => void;
}

/**
 * EnrichmentItem component - displays an enrichment in the sidebar.
 * Shows the enrichment's name, type icon, and status indicator.
 * Supports drag-and-drop to create groups by dropping one enrichment on another.
 */
const EnrichmentItem: React.FC<EnrichmentItemProps> = ({
  enrichment,
  onRunEnrichment,
  isSelected,
  orderedIds = [],
  onMultiSelectContextMenu,
  onDropToCreateGroup,
}) => {
  // Panel navigation hook for split-view compatible navigation
  const { navigateTo } = usePanelNavigation();

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  const isActive = enrichment.status === "active";
  const typeIcon = getEnrichmentIcon(enrichment.type);
  const typeLabel = getTypeLabel(enrichment.type);

  // Display name
  const displayName = enrichment.name || typeLabel || enrichment.id;

  // Create drag payload
  const dragPayload = createEnrichmentDragPayload(enrichment.id, displayName);

  const handleClick = useCallback(() => {
    navigateTo(`/enrichments?id=${enrichment.id}`, { title: displayName });
  }, [enrichment.id, displayName, navigateTo]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleRun = useCallback(() => {
    onRunEnrichment?.(enrichment);
  }, [enrichment, onRunEnrichment]);

  // Status indicator
  const statusIndicator = (
    <FaCircle
      size={6}
      className={
        isActive
          ? "text-green-500"
          : enrichment.status === "error"
          ? "text-red-500"
          : "text-gray-400"
      }
      title={isActive ? "Actif" : enrichment.status === "error" ? "Erreur" : "Inactif"}
    />
  );

  // Build hover actions
  const hoverActions = onRunEnrichment ? (
    <SidebarItemAction
      onClick={handleRun}
      title="Exécuter"
      icon={<FaPlay size={10} />}
      variant="success"
    />
  ) : null;

  return (
    <>
      <SidebarItem
        id={enrichment.id}
        name={displayName}
        resourceType="enrichment"
        icon={<FontAwesomeIcon icon={typeIcon} size="sm" className="text-purple-400" />}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        isSelected={isSelected}
        orderedIds={orderedIds}
        onMultiSelectContextMenu={onMultiSelectContextMenu}
        enableDrag={true}
        onDropToCreateGroup={onDropToCreateGroup}
        dragPayload={dragPayload}
        hoverActions={hoverActions}
        statusIndicator={statusIndicator}
      />

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <ResourceContextMenu
            position={contextMenu}
            dragPayload={dragPayload}
            onClose={closeContextMenu}
          />
        )}
      </AnimatePresence>
    </>
  );
};

EnrichmentItem.displayName = "EnrichmentItem";

export default EnrichmentItem;
